/**
 * Products listing page with advanced filtering and pagination
 * Follows the unified UX pattern with stats, filtering, and modern design
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getProductsSummary, ProductSummary } from '../services/staticContent';
import { ProductCategory, Product, Currency } from '../types';
import { getProductImageUrl } from '../utils/imageUtils';
import { FilterBar, FilterConfig } from '../components/shared/FilterBar';
import { Pagination } from '../components/shared/Pagination';
import { usePublicProductFilters } from '../hooks/usePublicFilters';
import { filterAndPaginateProducts, FilteredProductsResult } from '../utils/productFiltering';
import { StatsWidget, StatItem } from '../components/common/StatsWidget';
import { ToastContainer, ToastData } from '../components/common/Toast';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useOwnedProducts } from '../hooks/useOwnedProducts';
import {
  Package,
  Tag,
  Star,
  RefreshCw,
  HelpCircle,
  AlertCircle,
  CheckCircle,
  Plus,
  Download,
} from 'lucide-react';

export default function ProductsPage(): JSX.Element {
  const [allProducts, setAllProducts] = useState<ProductSummary[]>([]);
  const [filteredResult, setFilteredResult] = useState<FilteredProductsResult>({
    products: [],
    total: 0,
    currentPage: 1,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const { items: cartItems, addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const { isOwned } = useOwnedProducts();
  const { filters, debouncedFilters, setFilter, clearFilters, isFiltering } = usePublicProductFilters();

  // Check if product is in cart
  const isInCart = useCallback((productId: string) => {
    return cartItems.some(item => item.productId === productId);
  }, [cartItems]);

  // Handle add to cart with toast notification
  const handleAddToCart = useCallback((product: ProductSummary, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isInCart(product.id)) return;

    // Convert ProductSummary to Product for cart
    const cartProduct: Product = {
      id: product.id,
      title: product.title,
      shortDescription: product.shortDescription,
      longDescription: '',
      category: product.category,
      qualityTier: product.qualityTier,
      price: {
        amount: product.price.amount,
        currency: product.price.currency as Currency,
      },
      imageUrl: product.imageUrl,
      published: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    addToCart(cartProduct);

    // Show toast
    const newToast: ToastData = {
      id: `toast-${Date.now()}`,
      message: 'Added to cart!',
      type: 'success',
      action: {
        label: 'View Cart',
        href: '/cart',
      },
    };
    setToasts(prev => [...prev, newToast]);
  }, [addToCart, isInCart]);

  // Dismiss toast
  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Filter configuration for FilterBar
  const productFilters: FilterConfig[] = [
    {
      type: 'search',
      label: 'Search',
      key: 'search',
      placeholder: 'Search products by title or description...',
      width: 'lg',
    },
    {
      type: 'select',
      label: 'Category',
      key: 'category',
      width: 'md',
      options: [
        { value: '', label: 'All Categories' },
        { value: ProductCategory.HorseRacing, label: 'Horse Racing' },
        { value: ProductCategory.GreyhoundRacing, label: 'Greyhound Racing' },
        { value: ProductCategory.Lottery, label: 'Lottery' },
        { value: ProductCategory.CardGames, label: 'Card Games' },
        { value: ProductCategory.Roulette, label: 'Roulette' },
        { value: ProductCategory.MultiGame, label: 'Multi-Game' },
      ],
    },
    {
      type: 'select',
      label: 'Quality',
      key: 'tier',
      width: 'md',
      options: [
        { value: '', label: 'All Tiers' },
        { value: 'basic', label: 'Basic' },
        { value: 'premium', label: 'Premium' },
      ],
    },
    {
      type: 'number-range',
      label: 'Price Range',
      key: 'price',
      width: 'md',
    },
    {
      type: 'select',
      label: 'Sort By',
      key: 'sortBy',
      width: 'sm',
      options: [
        { value: 'publishedAt', label: 'Newest First' },
        { value: 'title', label: 'Title (A-Z)' },
        { value: 'price', label: 'Price' },
      ],
    },
  ];

  // Calculate stats from products
  const stats = useMemo(() => {
    const categories = new Set(allProducts.map(p => p.category)).size;
    const premiumCount = allProducts.filter(p => p.qualityTier === 'premium').length;
    const avgPrice = allProducts.length > 0
      ? allProducts.reduce((sum, p) => sum + p.price.amount, 0) / allProducts.length
      : 0;

    return {
      total: allProducts.length,
      categories,
      premium: premiumCount,
      avgPrice,
    };
  }, [allProducts]);

  // Stat items for the widget
  const statItems: StatItem[] = useMemo(() => [
    {
      id: 'total',
      label: 'Total Products',
      value: stats.total,
      icon: Package,
      variant: 'primary',
    },
    {
      id: 'categories',
      label: 'Categories',
      value: stats.categories,
      icon: Tag,
      variant: 'default',
    },
    {
      id: 'premium',
      label: 'Premium Guides',
      value: stats.premium,
      icon: Star,
      variant: 'warning',
    },
  ], [stats]);

  // Fetch all products once on mount
  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await getProductsSummary();
      setAllProducts(data);
    } catch (err: any) {
      console.error('Failed to load products:', err);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters whenever debouncedFilters change
  useEffect(() => {
    if (allProducts.length > 0) {
      const result = filterAndPaginateProducts(allProducts, debouncedFilters);
      setFilteredResult(result);
    }
  }, [allProducts, debouncedFilters]);

  return (
    <div className="container-custom py-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100">
            Strategy Guides
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Discover our collection of premium gambling strategy guides and systems
          </p>
        </div>
        <button
          onClick={loadProducts}
          disabled={loading}
          className="btn btn-secondary inline-flex items-center gap-2"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Section */}
      <section className="mb-8">
        <StatsWidget stats={statItems} columns={3} loading={loading} />
      </section>

      {/* Filter Bar */}
      <FilterBar
        filters={productFilters}
        values={filters}
        onChange={setFilter}
        onClear={clearFilters}
        resultsCount={filteredResult.total}
        collapsible={true}
      />

      {/* Loading State */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card overflow-hidden animate-pulse">
              <div className="w-full h-48 bg-gray-200 dark:bg-gray-700" />
              <div className="p-6">
                <div className="flex gap-2 mb-3">
                  <div className="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                </div>
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-4" />
                <div className="flex justify-between items-center">
                  <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mt-6 p-6 rounded-xl flex items-start gap-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertCircle size={24} className="text-red-600 dark:text-red-400 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-red-800 dark:text-red-200">Failed to Load Products</h3>
            <p className="text-red-700 dark:text-red-300 text-sm mt-1">{error}</p>
            <button
              onClick={loadProducts}
              className="mt-3 btn btn-primary text-sm"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Products Grid */}
      {!loading && !error && (
        <>
          {filteredResult.total === 0 ? (
            <div className="mt-6 card p-12 text-center">
              <Package size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {isFiltering ? 'No Products Found' : 'No Products Available'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {isFiltering
                  ? 'No products match your filters. Try adjusting your search criteria.'
                  : 'No products available at this time. Check back soon!'}
              </p>
              {isFiltering && (
                <button onClick={clearFilters} className="btn btn-secondary">
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                {filteredResult.products.map((product) => {
                  const owned = isOwned(product.id);
                  const inCart = isInCart(product.id);
                  return (
                    <div
                      key={product.id}
                      className="card overflow-hidden group hover:shadow-lg transition-all duration-300 flex flex-col"
                    >
                      <Link to={`/products/${product.id}`} className="block">
                        <div className="relative overflow-hidden">
                          <img
                            src={getProductImageUrl(product.imageUrl)}
                            alt={product.title}
                            className="w-full h-48 object-cover bg-gray-200 dark:bg-gray-700 group-hover:scale-105 transition-transform duration-300"
                          />
                          {/* Owned Badge */}
                          {owned && (
                            <div className="absolute top-3 left-3 px-2 py-1 bg-green-600 text-white text-xs font-bold rounded-full flex items-center gap-1">
                              <CheckCircle size={12} />
                              Owned
                            </div>
                          )}
                          {product.qualityTier === 'premium' && (
                            <div className="absolute top-3 right-3 px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                              <Star size={12} fill="currentColor" />
                              Premium
                            </div>
                          )}
                        </div>

                        <div className="p-6 pb-0 flex-grow flex flex-col">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xs px-2.5 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-full font-medium">
                              {getCategoryName(product.category)}
                            </span>
                            <span className="text-xs px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full capitalize">
                              {product.qualityTier}
                            </span>
                          </div>

                          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2 min-h-[3.5rem] group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                            {product.title}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2 min-h-[2.5rem]">
                            {product.shortDescription}
                          </p>
                        </div>
                      </Link>

                      {/* Price and Action Button */}
                      <div className="p-6 pt-0 mt-auto">
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                          <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                            {owned ? (
                              <span className="text-green-600 dark:text-green-400">Purchased</span>
                            ) : (
                              formatPrice(product.price.amount, product.price.currency)
                            )}
                          </span>
                          {owned ? (
                            <Link
                              to="/user?tab=products"
                              className="p-2.5 text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                              title="Download"
                            >
                              <Download size={20} />
                            </Link>
                          ) : inCart ? (
                            <Link
                              to="/cart"
                              className="p-2.5 text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                              title="View Cart"
                            >
                              <CheckCircle size={20} />
                            </Link>
                          ) : (
                            <button
                              onClick={(e) => handleAddToCart(product, e)}
                              className="p-2.5 text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                              title="Add to Cart"
                            >
                              <Plus size={20} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {filteredResult.totalPages > 1 && (
                <div className="mt-8">
                  <Pagination
                    currentPage={filteredResult.currentPage}
                    totalPages={filteredResult.totalPages}
                    totalItems={filteredResult.total}
                    itemsPerPage={filters.limit}
                    onPageChange={(page) => setFilter('page', page)}
                    onItemsPerPageChange={(limit) => setFilter('limit', limit)}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Help CTA */}
      <section className="mt-12 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-primary-100 dark:border-primary-800">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Need help choosing a guide?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Visit our help center for buying guides and recommendations
              </p>
            </div>
          </div>
          <Link to="/help" className="btn btn-primary whitespace-nowrap inline-flex items-center gap-2">
            <HelpCircle size={16} />
            Browse Help Center
          </Link>
        </div>
      </section>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

function getCategoryName(category: string): string {
  const names: Record<string, string> = {
    [ProductCategory.HorseRacing]: 'Horse Racing',
    [ProductCategory.GreyhoundRacing]: 'Greyhound Racing',
    [ProductCategory.Lottery]: 'Lottery',
    [ProductCategory.CardGames]: 'Card Games',
    [ProductCategory.Roulette]: 'Roulette',
    [ProductCategory.MultiGame]: 'Multi-Game',
  };

  return names[category] || category;
}
